import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../../../../constants';
import { SalesOverviewResponse } from '../../../../../hooks';
import { useCurrencyTools } from '../../../../../hooks/useCurrencyTools';
import { Icons } from '../../../../../stories/atoms';
import { areaOverviewTranslations } from '../../../../../translations/areaOverview';
import { OverviewWidget } from '../../../../areaOverview/overviewWidget/OverviewWidget';
import { PerformanceDrawerContent } from '../../../../areaOverview/overviewWidget/performanceDrawerContent/PerformanceDrawerContent';
import { AreaWidgetType } from '../../../../areaOverview/util';

type SalesOverviewWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  data: SalesOverviewResponse | null;
  isLoading: boolean;
  isError: boolean;
  errorRetryHandler: () => void;
};

export const SalesOverviewWidget: FC<SalesOverviewWidgetProps> = observer(
  ({ interval, currentDate, data, isLoading, isError, errorRetryHandler }) => {
    const { t } = useTranslation();
    const { getFormattedCurrency } = useCurrencyTools(interval, currentDate);

    const nonScoSales = data ? data.regularSales - data.scoSales : 0;
    const nonScoSalesLW = data ? (data.regularSalesLW ?? 0) - (data.scoSalesLW ?? 0) : 0;
    const nonScoSalesLY = data ? (data.regularSalesLY ?? 0) - (data.scoSalesLY ?? 0) : 0;

    const { salesOverview } = areaOverviewTranslations;
    const compact = { notation: 'compact' as const };

    const sliderContent = data ? (
      <PerformanceDrawerContent
        title={t(
          areaOverviewTranslations.performance.drawerTitle.key,
          areaOverviewTranslations.performance.drawerTitle.defaultValue,
        )}
        items={[
          {
            label: t(salesOverview.headerText.key, salesOverview.headerText.defaultValue),
            currentValue: data.regularSales,
            currentValueFormatted: getFormattedCurrency(data.regularSales, compact),
            lastWeek: data.regularSalesLW,
            lastWeekFormatted: getFormattedCurrency(data.regularSalesLW, compact),
            lastYear: data.regularSalesLY,
            lastYearFormatted: getFormattedCurrency(data.regularSalesLY, compact),
            isEffectiveHours: false,
          },
          {
            label: t(salesOverview.regularSalesText.key, salesOverview.regularSalesText.defaultValue),
            currentValue: nonScoSales,
            currentValueFormatted: getFormattedCurrency(nonScoSales, compact),
            lastWeek: nonScoSalesLW,
            lastWeekFormatted: getFormattedCurrency(nonScoSalesLW, compact),
            lastYear: nonScoSalesLY,
            lastYearFormatted: getFormattedCurrency(nonScoSalesLY, compact),
            isEffectiveHours: false,
          },
          {
            label: t(salesOverview.scoSalesText.key, salesOverview.scoSalesText.defaultValue),
            currentValue: data.scoSales,
            currentValueFormatted: getFormattedCurrency(data.scoSales, compact),
            lastWeek: data.scoSalesLW,
            lastWeekFormatted: getFormattedCurrency(data.scoSalesLW, compact),
            lastYear: data.scoSalesLY,
            lastYearFormatted: getFormattedCurrency(data.scoSalesLY, compact),
            isEffectiveHours: false,
          },
        ]}
      />
    ) : undefined;

    return (
      <OverviewWidget
        id={AreaWidgetType.SalesOverview}
        isLoading={isLoading}
        isError={isError}
        errorRetryHandler={errorRetryHandler}
        title={t(salesOverview.title.key, salesOverview.title.defaultValue)}
        headerIcon={Icons.SALESEFFICIENCY}
        headerText={t(salesOverview.headerText.key, salesOverview.headerText.defaultValue)}
        headerValue={getFormattedCurrency(data?.regularSales ?? 0)}
        headerComparison={
          data
            ? {
                current: data.regularSales,
                lastWeek: data.regularSalesLW,
                lastWeekFormatted: getFormattedCurrency(data.regularSalesLW),
                lastYear: data.regularSalesLY,
                lastYearFormatted: getFormattedCurrency(data.regularSalesLY),
                isEffectiveHours: false,
              }
            : undefined
        }
        widgetText={t(salesOverview.widgetText.key, salesOverview.widgetText.defaultValue)}
        statistics={[
          {
            label: t(salesOverview.regularSalesText.key, salesOverview.regularSalesText.defaultValue),
            value: getFormattedCurrency(nonScoSales ?? 0),
            comparison: data
              ? {
                  current: nonScoSales,
                  lastWeek: nonScoSalesLW,
                  lastWeekFormatted: getFormattedCurrency(nonScoSalesLW),
                  lastYear: nonScoSalesLY,
                  lastYearFormatted: getFormattedCurrency(nonScoSalesLY),
                  isEffectiveHours: false,
                }
              : undefined,
          },
          {
            label: t(salesOverview.scoSalesText.key, salesOverview.scoSalesText.defaultValue),
            value: getFormattedCurrency(data?.scoSales ?? 0),
            comparison: data
              ? {
                  current: data.scoSales,
                  lastWeek: data.scoSalesLW,
                  lastWeekFormatted: getFormattedCurrency(data.scoSalesLW),
                  lastYear: data.scoSalesLY,
                  lastYearFormatted: getFormattedCurrency(data.scoSalesLY),
                  isEffectiveHours: false,
                }
              : undefined,
          },
        ]}
        sliderContent={sliderContent}
      />
    );
  },
);
